import { AfterViewInit, CUSTOM_ELEMENTS_SCHEMA, ChangeDetectionStrategy, Component, ElementRef, computed, contentChildren, input, output, signal, viewChild } from '@angular/core'
import { NgTemplateOutlet } from '@angular/common'

import { SwiperContainer } from 'swiper/element'
import { SwiperOptions } from 'swiper/types'

import { SlideItemDirective } from '@shared/directives'
import { swiperOptions } from './swiper.options'

@Component({
  selector: 'slider',
  imports: [NgTemplateOutlet],
  templateUrl: './slider.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class Slider implements AfterViewInit {
  pagination = input<boolean>(false)
  navigation = input<boolean>(false)
  spaceBetween = input<number>(20)
  breakpoints = input<Record<string, SwiperOptions> | undefined>(undefined)
  allowTouchMove = input<boolean>(true)
  slidesPerView = input<number | 'auto'>(1)
  hashNavigation = input<boolean>(false)
  autoHeight = input<boolean>(false)
  slidesPerGroup = input<number>(1)

  slider = viewChild<ElementRef<SwiperContainer>>('slider')

  readonly sliderId = `slider-${Math.random().toString(36).substring(2, 9)}`
  readonly slidesCount = computed(() => this.slideItems().length)

  readonly currentSlide = signal(0)
  readonly isFirstSlide = computed(() => this.currentSlide() === 0)
  readonly isLastSlide = computed(() => this.currentSlide() === this.slidesCount() - 1)

  slideChange = output<number>()

  slideItems = contentChildren(SlideItemDirective)

  next(): void {
    const swiper = this.slider()?.nativeElement.swiper
    if (swiper) {
      swiper.slideNext()
    }

    this.scrollToTop()
  }

  prev(): void {
    const swiper = this.slider()?.nativeElement.swiper
    if (swiper) {
      swiper.slidePrev()
    }

    this.scrollToTop()
  }

  updateAutoHeight(): void {
    this.slider()?.nativeElement.swiper?.updateAutoHeight()
  }

  private scrollToTop(): void {
    // Encuentra el contenedor con scroll (el padre con overflow-y-auto)
    const sliderElement = this.slider()?.nativeElement
    if (!sliderElement) return

    const scrollContainer = sliderElement.closest('.overflow-y-auto')
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  ngAfterViewInit(): void {
    const slider = this.slider()
    if (!slider) return

    const sliderOptions = { ...swiperOptions }

    if (this.navigation()) {
      sliderOptions.navigation = true
    }

    if (this.pagination()) {
      sliderOptions.pagination = {
        clickable: true,
        renderBullet: function (index: number, className: string) {
          return `<span class="${className}"></span>`
        },
        el: `#${this.sliderId}`
      }
    }

    if (this.breakpoints()) {
      sliderOptions.breakpoints = this.breakpoints()
    }

    sliderOptions.spaceBetween = this.spaceBetween()
    sliderOptions.allowTouchMove = this.allowTouchMove()
    sliderOptions.slidesPerView = this.slidesPerView()
    sliderOptions.autoHeight = this.autoHeight()
    sliderOptions.slidesPerGroup = this.slidesPerGroup()

    if (this.hashNavigation()) {
      sliderOptions.hashNavigation = {
        watchState: true
      }
    }

    Object.assign(slider.nativeElement, sliderOptions)

    slider.nativeElement.initialize()

    slider.nativeElement.swiper?.on('slideChange', swiper => {
      this.onSlideChange(swiper.realIndex)
    })
  }

  private onSlideChange(index: number): void {
    this.currentSlide.set(index)
    this.slideChange.emit(index)
  }
}
